import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'
import { createElement } from 'react'
import ptBR from './pt-BR.json'
import en from './en.json'

export type Locale = 'pt-BR' | 'en'

type TranslationMap = Record<string, string>

const translations: Record<Locale, TranslationMap> = {
  'pt-BR': ptBR,
  en: en,
}

/** Interpolation params for `t(key, { foo: 'bar' })` → replaces `{{foo}}`. */
export type TranslationParams = Record<string, string | number>

interface I18nContextValue {
  locale: Locale
  setLocale: (locale: Locale) => void
  t: (key: string, params?: TranslationParams) => string
}

const I18nContext = createContext<I18nContextValue | null>(null)

function detectLocale(): Locale {
  const stored = localStorage.getItem('hub-wifi-locale')
  if (stored === 'en' || stored === 'pt-BR') return stored
  const browser = navigator.language
  if (browser.startsWith('en')) return 'en'
  return 'pt-BR'
}

// Replace `{{key}}` placeholders with the supplied params. Unknown
// placeholders are left as-is so missing data surfaces visibly rather than
// printing `undefined`.
function interpolate(template: string, params?: TranslationParams): string {
  if (!params) return template
  return template.replace(/\{\{(\w+)\}\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : match,
  )
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale)

  const setLocale = useCallback((newLocale: Locale) => {
    localStorage.setItem('hub-wifi-locale', newLocale)
    setLocaleState(newLocale)
  }, [])

  const t = useCallback(
    (key: string, params?: TranslationParams): string => {
      const raw = translations[locale][key] ?? key
      return interpolate(raw, params)
    },
    [locale],
  )

  return createElement(I18nContext.Provider, { value: { locale, setLocale, t } }, children)
}

export function useTranslation() {
  const context = useContext(I18nContext)
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider')
  }
  return context
}
